import React, { useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { RestaurantTable } from '../../manager/tables/types';

import { useAppSelector } from '../../../app/store/configureStore';
import { useAppDispatch } from '../../../app/store/configureStore';
import LoadingComponent from '../../../app/layout/LoadingComponent';
import ListTitleBox from '../../../app/components/list/ListTitleBox';
import EmptyScreen from '../../../app/components/EmptyScreen';
import { TableHeader } from '../../manager/tables/components/TableList/TableHeader';
import { TableRow } from '../../manager/tables/components/TableList/TableRow';
import { useTableActions } from '../../manager/tables/hooks/useTableActions';
import {
  fetchWaiterTableListAsync,
  waiterTableSelectors,
} from './waiterTableListSlice';

export const WaiterTableListPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const { loading, target } = useTableActions(restaurantId);

  const tables = useAppSelector(waiterTableSelectors.selectAll);
  const { status } = useAppSelector((state) => state.waiterTables);

  useEffect(() => {
    const loadTables = async () => {
      try {
        await dispatch(fetchWaiterTableListAsync({ restaurantId })).unwrap();
      } catch (error) {
        console.error('Failed to fetch tables:', error);
      }
    };

    loadTables();
  }, [dispatch, restaurantId]);

  const handleQrCode = React.useCallback(
    (table: RestaurantTable) => {
      navigate(
        `/restaurant-waiter/restaurants/${restaurantId}/tables/qr-code?tableNumber=${table.tableNumber}`
      );
    },
    [navigate, restaurantId]
  );

  const renderContent = () => {
    if (status === 'loading') {
      return (
        <LoadingComponent
          header={<ListTitleBox title={t('table.list.title')} />}
        />
      );
    }

    if (!tables || tables.length === 0) {
      return (
        <EmptyScreen
          title={t('table.list.title')}
          emptyText={t('table.list.empty')}
        />
      );
    }

    return (
      <>
        <ListTitleBox title={t('table.list.title')} />
        <TableContainer component={Paper} sx={{ width: '100%', mb: 2 }}>
          <Table aria-label={t('table.list.title')}>
            <TableHead>
              <TableHeader />
            </TableHead>
            <TableBody>
              {tables.map((table: RestaurantTable, index: number) => (
                <TableRow
                  key={table.id}
                  table={table}
                  restaurantId={restaurantId}
                  index={index}
                  loading={loading}
                  target={target}
                  onQrCode={handleQrCode}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  return <>{renderContent()}</>;
};
